<template>
    <div class="flex w-full flex-col items-center gap-6 max-w-5xl">
        <div class="text-center leading-tight">
            <h2 class="max-w-2xl display-medium text-balance mb-2">
                <span class="text-ink">{{ t("onboarding.evaluator.session20Preview.title") }}</span>
            </h2>
            <p class="text-ink-muted body md:display-serif max-w-2xl mx-auto text-pretty">{{ t("onboarding.evaluator.session20Preview.subtitle") }}</p>
        </div>

        <!-- Miniature browser/product illustration, intentionally scaled independently of the live UI. -->
        <BaseCard padding="none" class="w-full overflow-hidden">
            <!-- Top bar -->
            <div class="flex items-center justify-between border-b border-border px-4 py-2.5 bg-surface-sunken/50">
                <div class="flex items-center gap-3">
                    <div class="flex gap-2">
                        <div class="h-3 w-3 rounded-full bg-red-400"></div>
                        <div class="h-3 w-3 rounded-full bg-yellow-400"></div>
                        <div class="h-3 w-3 rounded-full bg-green-400"></div>
                    </div>
                    <div class="rounded-md bg-surface-sunken px-3 py-1 text-xs text-ink-muted">{{ t("onboarding.evaluator.session20Preview.browserUrl") }}</div>
                </div>
            </div>

            <div class="flex max-h-[460px]">
                <!-- Sidebar -->
                <div class="w-48 shrink-0 border-r border-border bg-surface-raised p-4 flex flex-col gap-1">
                    <div class="flex items-center gap-2 mb-4">
                        <CoachingModeMarble class="w-6 h-6" />
                        <span class="text-sm font-semibold text-ink">{{ t("onboarding.evaluator.session20Preview.nav.brand") }}</span>
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg bg-nav-item-active-bg px-3 py-2 text-sm font-medium text-nav-item-active-text">
                        <i class="bi bi-house-door text-base" aria-hidden="true"></i> {{ t("onboarding.evaluator.session20Preview.nav.home") }}
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-nav-item-text hover:bg-nav-item-hover-bg">
                        <i class="bi bi-chat-dots text-base" aria-hidden="true"></i> {{ t("onboarding.evaluator.session20Preview.nav.sessions") }}
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-nav-item-text hover:bg-nav-item-hover-bg">
                        <i class="bi bi-lightning-charge text-base" aria-hidden="true"></i> {{ t("onboarding.evaluator.session20Preview.nav.myWork") }}
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-nav-item-text hover:bg-nav-item-hover-bg">
                        <i class="bi bi-people text-base" aria-hidden="true"></i> {{ t("onboarding.evaluator.session20Preview.nav.yourPeople") }}
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-nav-item-text hover:bg-nav-item-hover-bg">
                        <i class="bi bi-person text-base" aria-hidden="true"></i> {{ t("onboarding.evaluator.session20Preview.nav.profile") }}
                    </div>
                </div>

                <!-- Main content -->
                <div class="flex-1 p-6 overflow-y-auto">
                    <!-- Greeting -->
                    <div class="mb-6">
                        <h2 class="text-xl font-semibold text-ink mb-1">{{ t("onboarding.evaluator.session20Preview.greeting") }}</h2>
                        <p class="text-sm text-ink-muted">{{ t("onboarding.evaluator.session20Preview.greetingSubtitle") }}</p>
                    </div>

                    <!-- Proactive coaching card -->
                    <BaseCard theme="muted" class="mb-5">
                        <div class="flex items-start gap-3">
                            <div class="w-8 h-8 shrink-0 mt-1">
                                <CoachingModeMarble />
                            </div>
                            <div class="flex flex-col gap-2 flex-1">
                                <p class="text-sm text-ink leading-relaxed">
                                    {{ samplePrompts[activePromptIndex].message }}
                                </p>
                                <div class="flex flex-wrap gap-2">
                                    <SourceChip v-for="tag in samplePrompts[activePromptIndex].contextTags" :key="tag" class="text-ink-muted">{{ tag }}</SourceChip>
                                </div>
                            </div>
                        </div>
                        <!-- Prompt navigation dots -->
                        <div class="flex items-center justify-center gap-2 mt-3">
                            <BaseButton
                                v-for="(_, idx) in samplePrompts"
                                :key="idx"
                                theme="tertiary"
                                icon-only
                                size="sm"
                                :aria-label="t('onboarding.evaluator.session20Preview.viewExample', { number: idx + 1 })"
                                :aria-current="activePromptIndex === idx ? 'true' : undefined"
                                @click="activePromptIndex = idx">
                                <span
                                    class="h-1.5 rounded-full transition-[width,background-color]"
                                    :class="activePromptIndex === idx ? 'w-5 bg-progress-fill' : 'w-1.5 bg-progress-track'"
                                    aria-hidden="true" />
                            </BaseButton>
                        </div>
                    </BaseCard>

                    <!-- Insights row -->
                    <div>
                        <h3 class="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">{{ t("onboarding.evaluator.session20Preview.weeklyInsights") }}</h3>
                        <div class="grid grid-cols-3 gap-3">
                            <BaseCard v-for="insight in sampleInsights" :key="insight.title" padding="sm" class="flex flex-col gap-2">
                                <div class="flex items-center gap-2">
                                    <div class="h-1.5 w-1.5 rounded-full bg-ink-muted"></div>
                                    <span class="text-[11px] font-medium text-ink-muted">{{ insight.category }}</span>
                                </div>
                                <h4 class="text-xs font-semibold text-ink leading-snug">{{ insight.title }}</h4>
                                <p class="text-[11px] text-ink-muted leading-relaxed line-clamp-2">{{ insight.description }}</p>
                            </BaseCard>
                        </div>
                    </div>

                    <!-- Your People section -->
                    <div class="mt-5">
                        <h3 class="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">{{ t("onboarding.evaluator.session20Preview.nav.yourPeople") }}</h3>
                        <div class="flex flex-col gap-2.5">
                            <BaseCard v-for="person in peopleDynamics" :key="person.name" padding="sm" class="flex items-center gap-3">
                                <PeopleAvatar size="sm" :name="person.name" :initials="person.initials" />
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-semibold text-ink">{{ person.name }}</span>
                                        <span class="text-[10px] text-ink-muted">{{ person.role }}</span>
                                    </div>
                                    <p class="text-[11px] text-ink-muted leading-snug mt-1">{{ person.dynamic }}</p>
                                </div>
                                <BaseButton theme="tertiary" size="sm" class="whitespace-nowrap">{{ t("onboarding.evaluator.session20Preview.people.chatAboutThis") }}</BaseButton>
                            </BaseCard>
                        </div>
                    </div>
                </div>
            </div>
        </BaseCard>

        <div class="flex items-center gap-4 mt-2">
            <BaseButton theme="tertiary" @click="emit('back')"> <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> {{ t("onboarding.evaluator.shared.back") }} </BaseButton>
            <BaseButton theme="primary" class="w-60" @click="emit('next')"> {{ t("onboarding.evaluator.session20Preview.seeHowToEnable") }} </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const emit = defineEmits<{
    back: [];
    next: [];
}>();

const activePromptIndex = ref(0);

const samplePrompts = computed(() => [
    {
        message: t("onboarding.evaluator.session20Preview.prompts.prep.message"),
        contextTags: [
            t("onboarding.evaluator.session20Preview.prompts.prep.tags.jordan"),
            t("onboarding.evaluator.session20Preview.prompts.prep.tags.roadmap"),
            t("onboarding.evaluator.session20Preview.prompts.prep.tags.calendar"),
        ],
    },
    {
        message: t("onboarding.evaluator.session20Preview.prompts.delegation.message"),
        contextTags: [
            t("onboarding.evaluator.session20Preview.prompts.delegation.tags.growthPattern"),
            t("onboarding.evaluator.session20Preview.prompts.delegation.tags.delegationTheme"),
        ],
    },
    {
        message: t("onboarding.evaluator.session20Preview.prompts.performance.message"),
        contextTags: [
            t("onboarding.evaluator.session20Preview.prompts.performance.tags.alex"),
            t("onboarding.evaluator.session20Preview.prompts.performance.tags.performanceReview"),
            t("onboarding.evaluator.session20Preview.prompts.performance.tags.pastConversation"),
        ],
    },
]);

const peopleDynamics = computed(() => [
    {
        name: t("onboarding.evaluator.session20Preview.people.jordan.name"),
        initials: t("onboarding.evaluator.session20Preview.people.jordan.initials"),
        role: t("onboarding.evaluator.session20Preview.people.roles.directReport"),
        dynamic: t("onboarding.evaluator.session20Preview.people.jordan.dynamic"),
    },
    {
        name: t("onboarding.evaluator.session20Preview.people.sarah.name"),
        initials: t("onboarding.evaluator.session20Preview.people.sarah.initials"),
        role: t("onboarding.evaluator.session20Preview.people.roles.peer"),
        dynamic: t("onboarding.evaluator.session20Preview.people.sarah.dynamic"),
    },
    {
        name: t("onboarding.evaluator.session20Preview.people.alex.name"),
        initials: t("onboarding.evaluator.session20Preview.people.alex.initials"),
        role: t("onboarding.evaluator.session20Preview.people.roles.directReport"),
        dynamic: t("onboarding.evaluator.session20Preview.people.alex.dynamic"),
    },
    {
        name: t("onboarding.evaluator.session20Preview.people.dana.name"),
        initials: t("onboarding.evaluator.session20Preview.people.dana.initials"),
        role: t("onboarding.evaluator.session20Preview.people.roles.skipLevel"),
        dynamic: t("onboarding.evaluator.session20Preview.people.dana.dynamic"),
    },
]);

const sampleInsights = computed(() => [
    {
        category: t("onboarding.evaluator.session20Preview.insights.delegation.category"),
        title: t("onboarding.evaluator.session20Preview.insights.delegation.title"),
        description: t("onboarding.evaluator.session20Preview.insights.delegation.description"),
    },
    {
        category: t("onboarding.evaluator.session20Preview.insights.autonomy.category"),
        title: t("onboarding.evaluator.session20Preview.insights.autonomy.title"),
        description: t("onboarding.evaluator.session20Preview.insights.autonomy.description"),
    },
    {
        category: t("onboarding.evaluator.session20Preview.insights.priorities.category"),
        title: t("onboarding.evaluator.session20Preview.insights.priorities.title"),
        description: t("onboarding.evaluator.session20Preview.insights.priorities.description"),
    },
    {
        category: t("onboarding.evaluator.session20Preview.insights.values.category"),
        title: t("onboarding.evaluator.session20Preview.insights.values.title"),
        description: t("onboarding.evaluator.session20Preview.insights.values.description"),
    },
]);
</script>
